<template>
  <div class="modal-overlay" v-if="visible" @click.self="$emit('cancel')">
    <div class="modal-card form-modal">
      <h2 class="modal-title">{{ isEditing ? 'Editar acampante' : 'Novo acampante' }}</h2>

      <form @submit.prevent="handleSubmit" class="form">
        <div class="form-group">
          <label class="label" for="acampante-name">Nome completo *</label>
          <input
            id="acampante-name"
            class="input"
            type="text"
            v-model="form.name"
            placeholder="Ex: João da Silva"
            autocomplete="off"
          />
          <span class="error-msg" v-if="errors.name">{{ errors.name }}</span>
        </div>

        <div class="form-group">
          <label class="label" for="acampante-contato">Contato</label>
          <input
            id="acampante-contato"
            class="input"
            type="tel"
            v-model="form.contato"
            placeholder="(11) 99999-0000"
            autocomplete="off"
          />
        </div>

        <div class="form-group">
          <label class="label">Equipe</label>
          <EquipeSelector v-model="form.equipe" />
          <span class="error-msg" v-if="errors.equipe">{{ errors.equipe }}</span>
        </div>

        <div class="form-group">
          <label class="label" for="acampante-valor-total">Valor total (R$)</label>
          <input
            id="acampante-valor-total"
            class="input"
            type="text"
            inputmode="decimal"
            v-model="form.valorTotal"
            placeholder="320,00"
            autocomplete="off"
          />
          <span class="hint-msg">Total que este acampante deve pagar. Deixe em branco se ainda não definido.</span>
          <span class="error-msg" v-if="errors.valorTotal">{{ errors.valorTotal }}</span>
        </div>

        <div class="form-group">
          <label class="toggle">
            <input type="checkbox" v-model="form.negociado" />
            <span>🤝 Acordo de pagamento (negociado)</span>
          </label>
          <span class="hint-msg">Marque quando o acampante combinou pagar depois. Vai liberar o check-in mesmo com saldo a pagar.</span>
          <textarea
            v-if="form.negociado"
            class="input textarea"
            v-model="form.observacaoNegociacao"
            placeholder="Observação do acordo (data prevista, forma de pagamento, etc.)"
            maxlength="300"
            rows="2"
            style="margin-top: 8px;"
          ></textarea>
        </div>

        <div class="form-group">
          <label class="label">Participação</label>
          <div class="radio-row">
            <label class="radio-card" :class="{ active: form.participacao === 'COMPLETO' }">
              <input type="radio" value="COMPLETO" v-model="form.participacao" />
              <span>Completo (4 dias)</span>
            </label>
            <label class="radio-card" :class="{ active: form.participacao === 'DIARIA' }">
              <input type="radio" value="DIARIA" v-model="form.participacao" />
              <span>Diária</span>
            </label>
          </div>
          <div v-if="form.participacao === 'DIARIA'" class="dias-chips">
            <label
              v-for="d in DIAS_ACAMP"
              :key="d.value"
              class="chip"
              :class="{ active: form.dias.includes(d.value) }"
            >
              <input type="checkbox" :value="d.value" v-model="form.dias" />
              <span>{{ d.label }}</span>
            </label>
          </div>
          <span class="error-msg" v-if="errors.dias">{{ errors.dias }}</span>
          <span v-else-if="form.participacao === 'DIARIA' && form.dias.length === 0" class="hint-msg">
            Selecione ao menos um dia.
          </span>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="$emit('cancel')">Cancelar</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            <span v-if="!submitting">{{ isEditing ? 'Salvar' : 'Cadastrar' }}</span>
            <span v-else>Salvando...</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import EquipeSelector from './EquipeSelector.vue'
import { EQUIPES } from '../lib/equipes'
import { DIAS_ACAMP, sortDias } from '../lib/dias'

const EQUIPE_VALUES = EQUIPES.map(e => e.value)
const DIAS_VALUES = DIAS_ACAMP.map(d => d.value)

export default {
  name: 'AcampanteForm',
  components: { EquipeSelector },
  props: {
    visible: { type: Boolean, default: false },
    participant: { type: Object, default: null },
    submitting: { type: Boolean, default: false }
  },
  emits: ['save', 'cancel'],
  data() {
    return {
      form: this.emptyForm(),
      errors: {}
    }
  },
  watch: {
    visible(open) {
      if (!open) return
      this.errors = {}
      if (this.participant) {
        const dias = sortDias(this.participant.diasAcamp || [])
        this.form = {
          name: this.participant.name || '',
          contato: this.participant.contato || '',
          equipe: this.participant.equipe || null,
          valorTotal: this.participant.valorTotal != null
            ? String(this.participant.valorTotal).replace('.', ',')
            : '',
          participacao: dias.length > 0 ? 'DIARIA' : 'COMPLETO',
          dias,
          negociado: !!this.participant.negociado,
          observacaoNegociacao: this.participant.observacaoNegociacao || ''
        }
      } else {
        this.form = this.emptyForm()
      }
    }
  },
  computed: {
    isEditing() { return !!this.participant },
    DIAS_ACAMP: () => DIAS_ACAMP
  },
  methods: {
    emptyForm() {
      return {
        name: '', contato: '', equipe: null, valorTotal: '',
        participacao: 'COMPLETO', dias: [],
        negociado: false, observacaoNegociacao: ''
      }
    },
    parseValor(raw) {
      if (raw === null || raw === undefined) return null
      const s = String(raw).trim()
      if (!s) return null
      const n = Number(s.replace(/[R$\s]/g, '').replace(',', '.'))
      if (!Number.isFinite(n) || n < 0) return Number.NaN
      return Math.round(n * 100) / 100
    },
    validate() {
      const errors = {}
      const name = (this.form.name || '').trim()
      if (name.length < 2) errors.name = 'Nome deve ter ao menos 2 caracteres'
      if (this.form.equipe !== null && !EQUIPE_VALUES.includes(this.form.equipe)) {
        errors.equipe = 'Equipe inválida'
      }
      const valorTotal = this.parseValor(this.form.valorTotal)
      if (Number.isNaN(valorTotal)) errors.valorTotal = 'Valor inválido'
      if (this.form.participacao === 'DIARIA') {
        if (!this.form.dias || this.form.dias.length === 0) {
          errors.dias = 'Selecione ao menos um dia.'
        } else if (this.form.dias.some(d => !DIAS_VALUES.includes(d))) {
          errors.dias = 'Dia inválido.'
        }
      }
      this.errors = errors
      return Object.keys(errors).length === 0
    },
    handleSubmit() {
      if (!this.validate()) return
      const diasAcamp = this.form.participacao === 'DIARIA' ? sortDias(this.form.dias) : []
      this.$emit('save', {
        data: {
          name: this.form.name.trim(),
          contato: (this.form.contato || '').trim() || null,
          equipe: this.form.equipe,
          valorTotal: this.parseValor(this.form.valorTotal),
          diasAcamp,
          negociado: !!this.form.negociado,
          observacaoNegociacao: this.form.negociado
            ? ((this.form.observacaoNegociacao || '').trim() || null)
            : null
        }
      })
    }
  }
}
</script>

<style scoped>
.form-modal { max-width: 460px; }
.form { display: flex; flex-direction: column; gap: var(--spacing-base); }
.form-group { display: flex; flex-direction: column; }
.hint-msg {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  margin-top: var(--spacing-xs);
}

.toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-primary);
  cursor: pointer;
  user-select: none;
}
.toggle input { width: 18px; height: 18px; cursor: pointer; }
.textarea { resize: vertical; min-height: 50px; font-family: var(--font-primary); }

.radio-row { display: flex; gap: var(--spacing-sm); flex-wrap: wrap; }
.radio-card {
  flex: 1;
  min-width: 120px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  border: 2px solid #e0e0e0;
  border-radius: var(--radius-md);
  background: #fff;
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  transition: var(--transition-fast);
  color: var(--color-text-primary);
}
.radio-card input { display: none; }
.radio-card:hover { border-color: var(--color-accent); }
.radio-card.active {
  border-color: var(--color-accent);
  background: rgba(51, 102, 204, 0.08);
  color: var(--color-accent);
}

.dias-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: var(--spacing-sm);
}
.dias-chips .chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border: 1.5px solid #ddd;
  border-radius: 999px;
  background: #fff;
  font-size: var(--text-xs);
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  color: var(--color-text-secondary);
}
.dias-chips .chip input { display: none; }
.dias-chips .chip.active {
  border-color: var(--color-accent);
  background: rgba(51, 102, 204, 0.1);
  color: var(--color-accent);
}
</style>
